import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import Modal from 'components/_UI/Modal';
import Paginate from 'components/_UI/Paginate';
import { ArrowLeft, Gift } from 'react-feather';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import CreateDrawer from '../drawer/CreateDrawer';
import ListDrawer from '../drawer/ListDrawer';
import DetailDrawer from '../drawer/DetailDrawer';
import { useEvent } from 'context/events/Event';
import randomPickerApi from 'api/requests/randomPicker';

export default function GiveawaySection({ data, loading, pagination, onBack, toast, onSuccess, onPaginate }) {
    const { event } = useEvent();
    const [modalVisibleDrawer, setModalVisibleDrawer] = useState(false);
    const [modalDetailDrawer, setModalDetailDrawer] = useState(false);
    const [deletingGiveaway, setDeletingGiveaway] = useState(false);

    function toggleModalAction(action) {
        if (action === 'drawer') {
            setModalVisibleDrawer(true);
            return;
        }
        if (action.type === 'detailDrawer') {
            setModalDetailDrawer(action);
            return;
        }
        if (action.type === 'deleteDrawer') {
            setModalDetailDrawer(action);
            return;
        }
    }

    async function deleteGiveaway(id) {
        setDeletingGiveaway(true);
        try {
            await randomPickerApi.delete(event?.slug, id);
            setModalDetailDrawer(false);
            toast.success('Sorteio excluído com sucesso!', { position: 'top-center' });
            onSuccess();
        } catch (error) {
        } finally {
            setDeletingGiveaway(false);
        }
    }

    function handleDrawerSuccess() {
        setModalVisibleDrawer(false);
        setModalDetailDrawer(false);
        onSuccess();
    }

    const hasGiveaways = data?.length > 0 && data[0]?.winners?.length > 0;
    const count = hasGiveaways ? data.length : 0;

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={colors.secondaryPurple} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <Gift size={20} color={colors.occasionalPurple} />
                        </IconBadge>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>Sorteador</SectionTitle>
                                {count > 0 && <CountBadge>{count}</CountBadge>}
                            </TitleRow>
                            <SectionDescription>
                                Faça sorteios e distribua prêmios entre os participantes do seu evento.
                            </SectionDescription>
                        </HeaderText>
                    </HeaderLeft>

                    <ActionsRow>
                        <Button
                            variant='featured'
                            category='squired'
                            onClick={() => toggleModalAction('drawer')}
                        >
                            <Gift color={colors.neutralLight} size={16} />
                            <Text
                                color={colors.neutralLight}
                                paddingBottom={0}
                                marginLeft={'6px'}
                            >
                                Sortear Paricipantes
                            </Text>
                        </Button>
                    </ActionsRow>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : !hasGiveaways ? (
                    <EmptyState>
                        <EmptyIcon>
                            <Gift size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhum sorteio criado.
                        </Text>
                        <EmptyHint>
                            Crie um sorteio para premiar os participantes do seu evento.
                        </EmptyHint>
                    </EmptyState>
                ) : (
                    <ContentArea>
                        {data?.map(el => (
                            <ListDrawer
                                key={el.id}
                                giveaway={el}
                                isLoading={loading}
                                toggleAction={toggleModalAction}
                            />
                        ))}
                        {pagination.lastPage > 1 && (
                            <Paginate
                                perPage={pagination.perPage}
                                totalItems={pagination.totalItems}
                                lastPage={pagination.lastPage}
                                firstPage={pagination.firstPage}
                                currentPage={pagination.currentPage}
                                marginTop={'20px'}
                                onPaginate={onPaginate}
                            />
                        )}
                    </ContentArea>
                )}
            </SectionCard>

            {modalVisibleDrawer && (
                <CreateDrawer
                    visible={modalVisibleDrawer}
                    slug={event?.slug}
                    onSuccess={handleDrawerSuccess}
                    onClose={() => setModalVisibleDrawer(false)}
                />
            )}

            {modalDetailDrawer?.type === 'detailDrawer' ? (
                <DetailDrawer
                    modalDetailDrawer={modalDetailDrawer}
                    setModalDetailDrawer={setModalDetailDrawer}
                />
            ) : (
                modalDetailDrawer?.type === 'deleteDrawer' && (
                    <Modal isOpen={modalDetailDrawer?.type === 'deleteDrawer'} onClose={() => setModalDetailDrawer(false)}>
                        <Text>Tem certeza que deseja excluir esse sorteio?</Text>
                        {deletingGiveaway ? (
                            <Loader />
                        ) : (
                            <ModalActions>
                                <Button onClick={() => setModalDetailDrawer(false)} variant='light'>Cancelar</Button>
                                <Button onClick={() => deleteGiveaway(modalDetailDrawer?.item?.giveaway?.id)}>Confirmar</Button>
                            </ModalActions>
                        )}
                    </Modal>
                )
            )}
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${colors.secondaryPurple}44; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}12;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${colors.secondaryPurple};
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.night}0A;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;

const ContentArea = styled.div`
    display: flex;
    flex-direction: column;
`;

const ModalActions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
